/**
 * Quiz "Crie sua fórmula"
 *
 * Passos: tipo de cabelo -> textura -> objetivos (multi) -> fragrância -> Finalizar.
 *
 * Estrutura no DOM (ver blocks/quiz-formula.liquid):
 *   - <h3 data-quiz-step="chave" ...>  define cada passo (ordem = ordem do quiz)
 *   - [data-quiz-option][data-quiz-kind="chave"]  opções (blocos filhos), todas numa lista única;
 *     apenas as do passo atual ficam visíveis.
 *
 * Ao finalizar:
 *   1. grava as respostas como *cart attributes* (campos personalizados do pedido, independentes da nota);
 *   2. adiciona shampoo, condicionador e máscara da fórmula escolhida ao carrinho;
 *   3. salva as respostas no localStorage (aba "Recuperar minha fórmula");
 *   4. redireciona para /cart.
 */

const STORAGE_KEY = 'narte:quiz-formula';
const DEFAULT_PRODUCT_TYPES = ['SH', 'CO', 'MS', 'LI'];

class QuizFormula extends HTMLElement {
  /** @type {HTMLElement[]} */
  #steps = [];
  #stepIndex = 0;
  /** @type {Record<string, any>} */
  #answers = {};
  #busy = false;
  #advanceTimer = 0;

  connectedCallback() {
    this.#steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    this.addEventListener('click', this.#onClick);
    document.addEventListener('shopify:block:select', this.#onEditorSelect);

    this.#renderRecover();
    this.#goTo(0);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('shopify:block:select', this.#onEditorSelect);
    clearTimeout(this.#advanceTimer);
  }

  /* ------------------------------------------------------------------ */
  /* Eventos                                                             */
  /* ------------------------------------------------------------------ */

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const tab = target.closest('[data-quiz-tab]');
    if (tab) return this.#showPanel(tab.dataset.quizTab);

    if (target.closest('[data-quiz-back]')) return this.#goTo(this.#stepIndex - 1);

    const option = target.closest('[data-quiz-option]');
    if (option && this.contains(option)) return this.#select(option);

    if (target.closest('[data-quiz-continue]')) return this.#continue();
    if (target.closest('[data-quiz-finish]')) return this.#finish();
    if (target.closest('[data-quiz-redo]')) return this.#redo();
  };

  /** No editor de temas, ao clicar em uma opção, mostra o passo correspondente. */
  #onEditorSelect = (event) => {
    const el = /** @type {HTMLElement} */ (event.target);
    if (!this.contains(el)) return;
    const option = el.closest('[data-quiz-option]');
    if (!option) return;
    const index = this.#steps.findIndex((s) => s.dataset.quizStep === option.dataset.quizKind);
    if (index < 0) return;
    this.#showPanel('create');
    this.#goTo(index);
  };

  /* ------------------------------------------------------------------ */
  /* Navegação                                                           */
  /* ------------------------------------------------------------------ */

  #showPanel(name) {
    this.querySelectorAll('[data-quiz-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.quizPanel !== name;
    });
    this.querySelectorAll('[data-quiz-tab]').forEach((tab) => {
      const active = tab.dataset.quizTab === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
    });
    if (name === 'recover') this.#renderRecover();
  }

  #goTo(index) {
    clearTimeout(this.#advanceTimer);
    if (index < 0 || index >= this.#steps.length) return;
    this.#stepIndex = index;

    const step = this.#steps[index];
    const key = step.dataset.quizStep;

    // Título do passo
    this.#steps.forEach((s, i) => {
      s.hidden = i !== index;
    });

    // Opções: só as do passo atual
    this.#options().forEach((o) => {
      o.hidden = o.dataset.quizKind !== key;
    });

    // Layout da lista (grade para múltipla escolha)
    const list = this.querySelector('[data-quiz-options]');
    if (list) list.classList.toggle('quiz-formula__options--grid', step.dataset.quizLayout === 'grid');

    // Botões
    const back = this.querySelector('[data-quiz-back]');
    if (back) back.hidden = index === 0;

    const cont = this.querySelector('[data-quiz-panel="create"] [data-quiz-continue]');
    if (cont) cont.hidden = step.dataset.quizMulti !== 'true';

    const finish = this.querySelector('[data-quiz-panel="create"] [data-quiz-finish]');
    if (finish) finish.hidden = !(step.dataset.quizFinal === 'true' && this.#answers[key]);

    // Progresso
    this.querySelectorAll('[data-quiz-progress] span').forEach((seg, i) => {
      seg.classList.toggle('is-filled', i <= index);
    });

    this.#setError('');
  }

  get #currentStep() {
    return this.#steps[this.#stepIndex];
  }

  /** Opções do passo informado (ou todas). */
  #options(key) {
    const all = Array.from(this.querySelectorAll('[data-quiz-option]'));
    return key ? all.filter((o) => o.dataset.quizKind === key) : all;
  }

  /* ------------------------------------------------------------------ */
  /* Seleção                                                             */
  /* ------------------------------------------------------------------ */

  #select(option) {
    const step = this.#currentStep;
    const key = step && step.dataset.quizStep;
    if (!key || option.dataset.quizKind !== key) return;

    const multi = step.dataset.quizMulti === 'true';
    const options = this.#options(key);

    if (multi) {
      const max = Number(step.dataset.quizMax || 0);
      const selectedCount = options.filter((o) => o.classList.contains('is-selected')).length;
      const selecting = !option.classList.contains('is-selected');

      if (selecting && max > 0 && selectedCount >= max) {
        this.#setError((this.dataset.maxMessage || 'Você pode escolher até [n] objetivos.').replace('[n]', String(max)));
        return;
      }

      option.classList.toggle('is-selected');
      option.setAttribute('aria-pressed', option.classList.contains('is-selected') ? 'true' : 'false');
      const selected = options.filter((o) => o.classList.contains('is-selected'));
      this.#answers[key] = selected.map((o) => this.#optionData(o));

      // Ao atingir o máximo, as demais opções ficam visualmente bloqueadas.
      const full = max > 0 && selected.length >= max;
      options.forEach((o) => o.classList.toggle('is-disabled', full && !o.classList.contains('is-selected')));

      this.#setError('');
      return;
    }

    options.forEach((o) => {
      o.classList.toggle('is-selected', o === option);
      o.setAttribute('aria-pressed', o === option ? 'true' : 'false');
    });
    this.#answers[key] = this.#optionData(option);

    if (step.dataset.quizFinal === 'true') {
      // Último passo: revela o botão "Finalizar" em vez de avançar.
      const finish = this.querySelector('[data-quiz-panel="create"] [data-quiz-finish]');
      if (finish) finish.hidden = false;
      return;
    }

    this.#advanceTimer = window.setTimeout(() => this.#goTo(this.#stepIndex + 1), 220);
  }

  #optionData(option) {
    return {
      value: option.dataset.quizValue || '',
      code: option.dataset.quizCode || '',
      detail: option.dataset.quizDetail || '',
      altCode: option.dataset.quizAltCode || '',
      altTrigger: option.dataset.quizAltTrigger || '',
    };
  }

  /**
   * Código de fórmula do cabelo. Se o tipo tiver uma fórmula alternativa e o objetivo
   * que a ativa estiver entre os escolhidos (ex.: Ondulado + "Definição de cachos" -> CH),
   * usa a alternativa.
   */
  #resolveHairCode(answers) {
    const hair = answers.hair_type || {};
    const base = (hair.code || '').toUpperCase();
    const alt = (hair.altCode || '').toUpperCase();
    const trigger = (hair.altTrigger || '').trim().toLowerCase();
    if (!alt || !trigger) return base;

    const goals = Array.isArray(answers.goals) ? answers.goals : [];
    const hit = goals.some((g) => (g.value || '').trim().toLowerCase() === trigger);
    return hit ? alt : base;
  }

  #continue() {
    const step = this.#currentStep;
    const key = step.dataset.quizStep;
    const answer = this.#answers[key];
    const min = Number(step.dataset.quizMin || 1);

    if (!answer || (Array.isArray(answer) && answer.length < min)) {
      this.#setError(this.dataset.requiredMessage || 'Selecione ao menos uma opção.');
      return;
    }
    this.#goTo(this.#stepIndex + 1);
  }

  /* ------------------------------------------------------------------ */
  /* Finalizar                                                           */
  /* ------------------------------------------------------------------ */

  async #finish() {
    if (this.#busy) return;

    const answers = this.#collectAnswers();
    if (!answers) return;

    const missing = this.#steps
      .map((s) => s.dataset.quizStep)
      .filter((k) => !answers[k] || (Array.isArray(answers[k]) && !answers[k].length));
    if (missing.length) {
      this.#setError(this.dataset.incompleteMessage || 'Responda todas as perguntas para finalizar.');
      return;
    }

    const hair = this.#resolveHairCode(answers);
    const fragrance = (answers.fragrance.code || '').toUpperCase();
    const map = this.#variantMap();
    const items = [];
    const notFound = [];

    // Para cada produto da fórmula, procura no mapa do mais específico ao mais genérico:
    // SH.L.P -> SH.L -> SH (ex.: leave-in só varia por cabelo: LI.L)
    for (const type of this.#productTypes()) {
      const candidates = [`${type}.${hair}.${fragrance}`, `${type}.${hair}`, type];
      const key = candidates.find((k) => map[k]);
      if (key) items.push({ id: Number(map[key]), quantity: 1 });
      else notFound.push(candidates[0]);
    }

    if (notFound.length) {
      this.#setError(`${this.dataset.mapErrorMessage || 'Fórmula não encontrada:'} ${notFound.join(', ')}`);
      return;
    }

    const attributes = this.#buildAttributes(answers, hair, fragrance);
    const finishButtons = this.querySelectorAll('[data-quiz-finish]');

    this.#busy = true;
    finishButtons.forEach((b) => {
      b.setAttribute('aria-disabled', 'true');
      b.classList.add('is-loading');
    });

    try {
      // 1) Campos personalizados do pedido (cart attributes -> "Detalhes adicionais" no admin).
      await this.#post(this.dataset.updateUrl, { attributes });

      // 2) Shampoo + condicionador + máscara da fórmula.
      const added = await this.#post(this.dataset.addUrl, { items });
      if (added && added.status) throw new Error(added.description || added.message || 'Erro ao adicionar ao carrinho');

      // 3) Guarda para "Recuperar minha fórmula".
      this.#save({ answers, attributes, items, savedAt: new Date().toISOString() });

      // 4) Vai para o carrinho.
      window.location.href = this.dataset.redirectUrl || this.dataset.cartUrl || '/cart';
    } catch (error) {
      this.#setError(this.dataset.cartErrorMessage || 'Não foi possível adicionar os produtos. Tente novamente.');
      console.error('[quiz-formula]', error);
    } finally {
      this.#busy = false;
      finishButtons.forEach((b) => {
        b.removeAttribute('aria-disabled');
        b.classList.remove('is-loading');
      });
    }
  }

  /** Respostas da aba ativa: as atuais (Criar) ou as salvas (Recuperar). */
  #collectAnswers() {
    const recoverPanel = this.querySelector('[data-quiz-panel="recover"]');
    if (recoverPanel && !recoverPanel.hidden) {
      const saved = this.#load();
      return saved ? saved.answers : null;
    }
    return this.#answers;
  }

  #buildAttributes(answers, hair, fragrance) {
    const labels = {};
    this.#steps.forEach((s) => {
      labels[s.dataset.quizStep] = s.dataset.quizLabel || s.dataset.quizStep;
    });

    const goals = Array.isArray(answers.goals) ? answers.goals : [];
    const attributes = {
      [labels.hair_type]: answers.hair_type.value,
      [labels.texture]: answers.texture.value,
      [labels.goals]: goals.map((g) => g.value).join(', '),
      [labels.fragrance]: answers.fragrance.value,
      [this.dataset.formulaLabel || 'Fórmula']: `${hair}.${fragrance}`,
    };

    const actives = goals.map((g) => g.detail).filter(Boolean);
    if (actives.length && this.dataset.activesLabel) {
      attributes[this.dataset.activesLabel] = actives.join(', ');
    }
    return attributes;
  }

  /** Códigos dos produtos adicionados ao finalizar (setting "Produtos da fórmula"). */
  #productTypes() {
    const raw = (this.dataset.productTypes || '').split(/[,\s]+/).map((t) => t.trim().toUpperCase()).filter(Boolean);
    return raw.length ? raw : DEFAULT_PRODUCT_TYPES;
  }

  /** Lê o mapa "SH.L.F: 123456" do admin. Linhas iniciadas por # são ignoradas. */
  #variantMap() {
    const map = {};
    const raw = this.dataset.variantMap || '';
    raw.split(/[\r\n;]+/).forEach((line) => {
      const clean = line.trim();
      if (!clean || clean.startsWith('#')) return;
      const [key, value] = clean.split(/[:=]/).map((s) => (s || '').trim());
      if (key && /^\d+$/.test(value)) map[key.toUpperCase()] = value;
    });
    return map;
  }

  async #post(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(json.description || json.message || response.statusText);
    return json;
  }

  /* ------------------------------------------------------------------ */
  /* Recuperar minha fórmula                                             */
  /* ------------------------------------------------------------------ */

  #renderRecover() {
    const empty = this.querySelector('[data-quiz-recover-empty]');
    const summary = this.querySelector('[data-quiz-recover-summary]');
    const list = this.querySelector('[data-quiz-recover-list]');
    if (!empty || !summary || !list) return;

    const saved = this.#load();
    if (!saved) {
      empty.hidden = false;
      summary.hidden = true;
      return;
    }

    list.innerHTML = '';
    Object.entries(saved.attributes || {}).forEach(([label, value]) => {
      if (!value) return;
      const dt = document.createElement('dt');
      dt.textContent = label;
      const dd = document.createElement('dd');
      dd.textContent = value;
      list.append(dt, dd);
    });

    empty.hidden = true;
    summary.hidden = false;
  }

  #redo() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
    this.#answers = {};
    this.#options().forEach((o) => {
      o.classList.remove('is-selected', 'is-disabled');
      o.setAttribute('aria-pressed', 'false');
    });
    this.#showPanel('create');
    this.#goTo(0);
  }

  #save(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
  }

  #load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  #setError(message) {
    const el = this.querySelector('[data-quiz-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }
}

if (!customElements.get('quiz-formula')) {
  customElements.define('quiz-formula', QuizFormula);
}
